/** Feature flag for GIS Home road risk map layer. */
export const RISK_MAP_ENABLED = true

/**
 * Debounce (ms) before refetching road-network underlay after pan/zoom.
 * Risk choropleth is country-wide per year and does not refetch on bounds change.
 */
export const RISK_MAP_BOUNDS_DEBOUNCE_MS = 700

/** Minimum map zoom to fetch the gray road-network underlay (when enabled from toolbar). */
export const RISK_MAP_NETWORK_MIN_ZOOM = 11

/** Default top-list size when API settings.topListSize is missing. */
export const RISK_MAP_TOP_LIST_SIZE_DEFAULT = 10

/**
 * Resolve top-list size from API settings (clamped 1–100).
 * @param {unknown} value
 * @returns {number}
 */
export function resolveRiskMapTopListSize(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return RISK_MAP_TOP_LIST_SIZE_DEFAULT
  return Math.min(100, Math.max(1, Math.round(n)))
}

/** @deprecated use RISK_MAP_TOP_LIST_SIZE_DEFAULT / resolveRiskMapTopListSize */
export const RISK_MAP_TOP_TEN_SIZE = RISK_MAP_TOP_LIST_SIZE_DEFAULT

/** Fixed riskScore display / CSV fraction digits for per-km values. */
export const RISK_MAP_SCORE_DECIMALS = 3

/**
 * API stores score per meter; UI shows the equivalent per kilometre.
 * score_per_km = score_per_m × 1000
 */
export const RISK_MAP_SCORE_PER_KM_FACTOR = 1000

/**
 * Convert API risk score (per meter) to display units (per km).
 * @param {unknown} scorePerMeter
 * @returns {number}
 */
export function riskScorePerKm(scorePerMeter) {
  const n = Number(scorePerMeter ?? 0)
  const safe = Number.isFinite(n) ? n : 0
  return safe * RISK_MAP_SCORE_PER_KM_FACTOR
}

/**
 * Format risk score for UI (per km, fixed decimals).
 * @param {unknown} scorePerMeter API riskScore (weightedSum / denominator_m)
 * @returns {string}
 */
export function formatRiskScore(scorePerMeter) {
  return riskScorePerKm(scorePerMeter).toLocaleString(undefined, {
    minimumFractionDigits: RISK_MAP_SCORE_DECIMALS,
    maximumFractionDigits: RISK_MAP_SCORE_DECIMALS,
  })
}

/** Zoom level when centering the map on a risk segment from the top-ten list. */
export const RISK_MAP_FOCUS_ZOOM = 15

/**
 * TEMPORARY: show toolbox button that seeds validated Police crashes for risk-map demos.
 * Visible only with `npm start` (NODE_ENV=development); hidden in production builds.
 */
export const RISK_MAP_DEMO_SEED_ENABLED = process.env.NODE_ENV === 'development'

/** TEMPORARY: default / initial count for demo crash seed input. */
export const RISK_MAP_DEMO_SEED_COUNT = 10

/** TEMPORARY: max crashes allowed per demo seed click. */
export const RISK_MAP_DEMO_SEED_MAX = 100

export const RISK_MAP_CRASH_DATE_FROM = 'crash.Crash date.from'
export const RISK_MAP_CRASH_DATE_TO = 'crash.Crash date.to'

/**
 * Stats date filters covering a full calendar year (merged/validated set).
 * @param {number} year
 * @returns {Array<{ name: string, value: string }>}
 */
export function buildRiskMapYearFilters(year) {
  const y = Number(year)
  if (!Number.isFinite(y) || y < 1) return []
  return [
    { name: RISK_MAP_CRASH_DATE_FROM, value: `${y}-01-01` },
    { name: RISK_MAP_CRASH_DATE_TO, value: `${y}-12-31` },
  ]
}
